import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Award, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const PartnerCertificates: React.FC = () => {
  const { toast } = useToast();
  // Mock: Em produção, viria de uma API com filtro WHERE empresa_id = X
  const companyId = localStorage.getItem("partnerCompanyId") || "1";
  
  // Mock data - Em produção, viria de uma API
  const certificates = [
    {
      id: 1,
      title: "Empresa Carbono Neutro 2026",
      description: "Certificado de neutralização de carbono para o ano de 2026",
      issuedDate: "2026-01-15",
      validity: "2026-12-31",
      badge: "🏆",
      status: "Válido"
    },
    {
      id: 2,
      title: "Selo Verde - Impacto Positivo",
      description: "Reconhecimento por financiamento de árvores nativas",
      issuedDate: "2025-12-01",
      validity: "2026-12-31",
      badge: "🌱",
      status: "Válido"
    },
    {
      id: 3,
      title: "Parceiro Sustentável Banclima",
      description: "Certificado de parceria na plataforma Banclima",
      issuedDate: "2025-06-15",
      validity: "2026-06-15",
      badge: "⭐",
      status: "Válido"
    }
  ];

  const handleDownloadPDF = (certificateId: number) => {
    // Em produção, faria download do PDF real
    toast({
      title: "Download iniciado",
      description: "O certificado está sendo baixado...",
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Certificados</h1>
        <p className="text-gray-600 mt-1">Visualize e baixe seus certificados de impacto ambiental</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {certificates.map((certificate) => (
          <Card key={certificate.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="text-4xl">{certificate.badge}</div>
                <Award className="h-6 w-6 text-yellow-500" />
              </div>
              <CardTitle className="mt-4">{certificate.title}</CardTitle>
              <CardDescription>{certificate.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Emitido em:</span>
                  <span className="font-medium">{formatDate(certificate.issuedDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Válido até:</span>
                  <span className="font-medium">{formatDate(certificate.validity)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="font-medium text-green-600">{certificate.status}</span>
                </div>
              </div>
              <Button
                onClick={() => handleDownloadPDF(certificate.id)}
                className="w-full bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]"
              >
                <Download className="mr-2 h-4 w-4" />
                Baixar PDF
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Resumo de Impacto */}
      <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Resumo de Impacto
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Total de CO₂ Compensado</p>
              <p className="text-2xl font-bold text-gray-900">450,5 t</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Árvores Financiadas</p>
              <p className="text-2xl font-bold text-gray-900">2.500</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Moedas Distribuídas</p>
              <p className="text-2xl font-bold text-gray-900">125.000 MSC</p>
            </div>
          </div>
          <Button
            onClick={() => handleDownloadPDF(0)}
            className="mt-4 bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]"
          >
            <Download className="mr-2 h-4 w-4" />
            Baixar Relatório Completo (PDF)
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerCertificates;
